import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu as MenuIcon, X, Phone, MessageCircle } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

export default function Navbar() {
  const { settings, menus } = useSettings();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const orderedMenus = [...menus].sort((a, b) => a.order - b.order);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 left-0 right-0 z-50 bg-[color:var(--marupiara-obsidian)] shadow-xl py-3 transition-[padding] duration-300"
      style={{ borderBottom: "1px solid rgba(197, 160, 89, 0.25)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between gap-6">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 shrink-0">
          {settings.logo_url ? (
            <img src={settings.logo_url} alt={settings.site_name || "Marupiara"} className="h-11 w-11 rounded-full object-cover" style={{ background: "#0A0A0A" }} />
          ) : null}
          <div className="hidden sm:block">
            <div className="font-serif text-xl leading-none tracking-wide" style={{ color: "var(--marupiara-gold)" }}>
              {settings.site_name || "Marupiara"}
            </div>
            <div className="overline text-[10px]" style={{ color: "rgba(232, 208, 155, 0.7)" }}>
              Editoração
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {orderedMenus.map((m) => (
            m.external ? (
              <a
                key={m.id}
                href={m.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`nav-link-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm text-white hover:text-[color:var(--marupiara-gold-light)] transition-colors gold-underline"
              >
                {m.label}
              </a>
            ) : (
              <NavLink
                key={m.id}
                to={m.url}
                end={m.url === "/"}
                data-testid={`nav-link-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm transition-colors gold-underline ${isActive ? "text-[color:var(--marupiara-gold)]" : "text-white hover:text-[color:var(--marupiara-gold-light)]"}`
                }
              >
                {m.label}
              </NavLink>
            )
          ))}
          {settings.author_area_url ? (
            <a
              href={settings.author_area_url}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-link-area-do-autor"
              className="text-sm px-4 py-2 border border-[color:var(--marupiara-gold)] text-[color:var(--marupiara-gold)] hover:bg-[color:var(--marupiara-gold)] hover:text-black transition-colors"
            >
              {settings.author_area_label || "Acesso"}
            </a>
          ) : null}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          {settings.whatsapp ? (
            <a
              href={`https://wa.me/${settings.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="nav-whatsapp"
              className="flex items-center gap-2 text-sm text-white hover:text-[color:var(--marupiara-gold)]"
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
          ) : null}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-[color:var(--marupiara-gold)] p-2"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[color:var(--marupiara-obsidian)] border-t border-[color:var(--marupiara-gold)]/25">
          <div className="px-6 py-6 flex flex-col gap-4">
            {orderedMenus.map((m) => (
              m.external ? (
                <a
                  key={m.id}
                  href={m.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`mobile-nav-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-white py-2 border-b border-white/10"
                >
                  {m.label}
                </a>
              ) : (
                <NavLink
                  key={m.id}
                  to={m.url}
                  end={m.url === "/"}
                  data-testid={`mobile-nav-${m.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="text-white py-2 border-b border-white/10"
                >
                  {m.label}
                </NavLink>
              )
            ))}
            {settings.author_area_url ? (
              <a
                href={settings.author_area_url}
                target="_blank"
                rel="noreferrer"
                data-testid="mobile-nav-area-do-autor"
                className="mt-2 px-4 py-3 text-center border border-[color:var(--marupiara-gold)] text-[color:var(--marupiara-gold)]"
              >
                {settings.author_area_label || "Acesso"}
              </a>
            ) : null}
          </div>
        </div>
      )}
    </header>
  );
}
